import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { QButton, QStack, useTheme } from '@captivateiq/quotient';
import { useCallback, useRef, useState } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  DefaultTooltipContent,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts/types/component/Tooltip';

import { HistogramContainer } from './HistogramContainer.styles';
import { useHistogram } from './hooks/useHistogram';

import { HistogramProps } from '../../types';
import {
  getChartColors,
  displayLegend,
  formatLabel,
  X_AXIS_LABEL_ANGLE_STRAIGHT,
  X_AXIS_LABEL_ANGLE_ROTATED,
} from '../../utils/chartUtils';
import { ChartLegend } from '../BarChart/ChartLegend'; // Reuse the bar chart legend
import { useAutoRotateLabels } from '../../hooks/useAutoRotateLabels';

export const Histogram = ({ targetLine, onClick, ...chartProps }: HistogramProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
  const { data, formatValue, xAxisKeys, xAxisLabelSpacing, yAxisKeys, yAxisLabels, yAxisWidth } =
    useHistogram(chartProps);
  const currentBar = useRef('');

  // Auto-rotation logic
  const { containerRef, effectiveAngleLabels } = useAutoRotateLabels({
    data,
    xAxisKeys,
    yAxisWidth,
    // Histograms don't have hideXAxis/hideYAxis options, always show axes
    hideXAxis: false,
    hideYAxis: false,
    isHorizontal: false,
    angleLabels: false,
    showFullLabels: false,
  });

  /**
   * Histogram tooltip shows bin range and frequency count
   */
  const handleTooltipHover = useCallback(
    ({ active, payload }: TooltipProps<ValueType, NameType>) => {
      if (active && payload?.length && currentBar.current) {
        const matchingBar = payload.find((item) => item.dataKey === currentBar.current);
        const xAxisLabel = payload[0].payload[xAxisKeys[0]];
        if (matchingBar) {
          return (
            <DefaultTooltipContent
              label={`Bin: ${xAxisLabel}`}
              payload={[
                {
                  ...matchingBar,
                  value: formatValue(matchingBar.value),
                },
              ]}
            />
          );
        }
      }
      return null;
    },
    [formatValue, xAxisKeys],
  );

  return (
    <HistogramContainer ref={containerRef} data-testid="histogram">
      {displayLegend(yAxisKeys) && (
        <QStack alignItems="flex-start" direction="row" spacing={1} sx={{ mb: 4 }}>
          <QButton
            color="primary"
            data-testid="view-legend-button"
            size="small"
            startIcon={faList}
            variant="text"
            onClick={() => setIsLegendModalOpen(true)}
          >
            View legend
          </QButton>
        </QStack>
      )}
      <ResponsiveContainer key={yAxisKeys.length}>
        <RechartsBarChart
          barCategoryGap={0} // No gap between bars for histogram
          barGap={0} // No gap between series
          data={data}
          margin={{ bottom: xAxisLabelSpacing(xAxisKeys[0]), right: 25 }}
          onClick={onClick}
        >
          <CartesianGrid stroke={theme.palette.neutral.primary[100]} strokeDasharray="3 3" vertical={false} />
          <XAxis
            angle={effectiveAngleLabels ? X_AXIS_LABEL_ANGLE_ROTATED : X_AXIS_LABEL_ANGLE_STRAIGHT}
            dataKey={xAxisKeys[0]}
            padding={{ right: 10 }}
            stroke={theme.palette.neutral.primary[700]}
            tick={{
              fill: theme.palette.neutral.primary[700],
              textAnchor: effectiveAngleLabels ? 'start' : 'middle',
            }}
            tickFormatter={(value) => formatLabel(value)}
            tickLine={false}
          />
          <YAxis
            minTickGap={0.5}
            padding={{ top: 10 }}
            stroke={theme.palette.neutral.primary[700]}
            tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'end' }}
            tickFormatter={formatValue}
            tickLine={false}
            width={yAxisWidth}
          />
          <Tooltip content={handleTooltipHover} cursor={false} />

          {/* Render histogram bars */}
          {yAxisKeys?.map((dataKey, seriesIndex) => {
            const colors = chartColors[seriesIndex % chartColors.length];

            return (
              <Bar
                activeBar={{ fill: colors.hover }}
                dataKey={dataKey}
                fill={colors.default}
                key={dataKey}
                name={yAxisLabels[seriesIndex]}
                radius={[2, 2, 0, 0]} // Subtle rounded top corners
                onMouseOver={() => {
                  currentBar.current = dataKey;
                }}
              />
            );
          })}

          {/* Render target line */}
          {targetLine !== undefined && targetLine !== null && (
            <>
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.common.white}
                strokeLinecap="round"
                strokeWidth={4}
                y={targetLine}
              />
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.denotive.positive[600]}
                strokeLinecap="round"
                strokeWidth={2}
                y={targetLine}
              />
            </>
          )}
        </RechartsBarChart>
      </ResponsiveContainer>
      <ChartLegend
        chartColors={chartColors}
        labels={yAxisLabels}
        open={isLegendModalOpen}
        onClose={() => setIsLegendModalOpen(false)}
      />
    </HistogramContainer>
  );
};
